/**
 * W3C Trace Context for API requests (BPE-645).
 *
 * Each request to our own API carries a fresh `traceparent` header. ASP.NET
 * Core continues that trace, so the backend's spans (and its Rollbar item,
 * BPE-808) share the trace ID. When a request fails, the same ID goes on the
 * frontend Rollbar item, linking the two.
 *
 * Only add this to same-origin clients: a custom header on a cross-origin
 * request (e.g. authService's Front Door call) triggers a CORS preflight.
 */

const TRACEPARENT = 'traceparent';
// version-traceid-parentid-flags; see https://www.w3.org/TR/trace-context/
const TRACEPARENT_PATTERN = /^00-([0-9a-f]{32})-[0-9a-f]{16}-[0-9a-f]{2}$/;

// Structural slices of axios types, so this module needn't import axios
// (restricted to services/api.ts).
interface IRequestConfigLike {
  headers: { set(name: string, value: string): unknown };
}

interface IRequestLike {
  baseURL?: string;
  url?: string;
  method?: string;
  // AxiosHeaders.get is case-insensitive; a plain property read is not.
  headers?: { get(name: string): unknown };
}

interface IRequestErrorLike {
  config?: IRequestLike;
  response?: { status?: number };
  rollbarContext?: unknown;
}

function randomHex(bytes: number): string {
  const values = crypto.getRandomValues(new Uint8Array(bytes));
  return Array.from(values, (b) => b.toString(16).padStart(2, '0')).join('');
}

/** A new `traceparent` value: random trace and span IDs, sampled. */
export function newTraceparent(): string {
  // An all-zero ID is invalid; with 128 random bits it won't happen, but
  // regenerate rather than send something the backend would discard.
  let traceId = randomHex(16);
  while (/^0+$/.test(traceId)) traceId = randomHex(16);
  let spanId = randomHex(8);
  while (/^0+$/.test(spanId)) spanId = randomHex(8);
  return `00-${traceId}-${spanId}-01`;
}

/** The trace ID inside a `traceparent` value, or undefined if malformed. */
export function traceIdOf(traceparent: unknown): string | undefined {
  if (typeof traceparent !== 'string') return undefined;
  return TRACEPARENT_PATTERN.exec(traceparent)?.[1];
}

/** Request interceptor: start a new trace for this request. */
export function addTraceparent<T extends IRequestConfigLike>(config: T): T {
  config.headers.set(TRACEPARENT, newTraceparent());
  return config;
}

/**
 * The request's trace ID and shape, for an error's `rollbarContext`. Rollbar
 * (`addErrorContext`) merges that into the item if the error goes unhandled.
 * The path has no query string, which can carry search terms or email
 * addresses.
 */
export function traceContextFor(config: IRequestLike, status?: number) {
  return {
    trace_id: traceIdOf(config.headers?.get(TRACEPARENT)),
    request: {
      method: config.method?.toUpperCase(),
      path: `${config.baseURL ?? ''}${config.url ?? ''}`.split('?')[0],
      status,
    },
  };
}

/** Response error interceptor helper: put the failed request's trace context on the error. */
export function attachTraceContext(error: IRequestErrorLike | null | undefined): void {
  if (!error?.config) return;
  error.rollbarContext = traceContextFor(error.config, error.response?.status);
}
